import { ImportConfigurationEditFormsCoordinatorService } from '../../import-configuration-edit-forms-coordinator.service';
import { UserManagementService } from './../../../../../../base/user-management/user-management.service';
import { CustomValidatorsFactory } from './../../../../../../base/shared/validators/custom-validators-factory';
import { UserEditFormType } from './../../../../../../base/user-edit-form/user-edit-form-type';
import { UserInfoDTO, ClientDTO, Role, ImportConfigurationUserAssignmentDTO, CreateInterfaceUserDTO, UpdateInterfaceUserInfoDTO, FilterDTO, FilterOperator, FilterCondition } from './../../../../../../base/models/models.swagger.generated';
import { AvpCustomAdaptor } from './../../../../../../base/shared/syncfusion/avp-custom-adaptor';
import { DataManager } from '@syncfusion/ej2-data';
import { GridBaseComponent } from 'src/app/base/shared/grid-base/grid-base.component';
import { SpinnerService } from './../../../../../../base/spinner/spinner.service';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { FormBuilder, Validators, FormGroup } from '@angular/forms';
import { FilterSettingsModel, RowDataBoundEventArgs, GridModule } from '@syncfusion/ej2-angular-grids';
import { Component, OnInit, ViewChild } from '@angular/core';
import { UserEditFormComponent } from '../../../../../../base/user-edit-form/user-edit-form.component';
import { QueryBuilderModule } from '@syncfusion/ej2-angular-querybuilder';
import { ToolbarModule } from '@syncfusion/ej2-angular-navigations';
import { GridConstantsFactory } from 'src/app/base/shared/syncfusion/grid-constants-factory';

@Component({
  selector: 'app-configuration-form-general-grid',
  templateUrl: './configuration-form-general-grid.component.html',
  styleUrls: ['./configuration-form-general-grid.component.scss'],
  imports: [GridModule, ToolbarModule, QueryBuilderModule, UserEditFormComponent, TranslateModule]
})
export class ConfigurationFormGeneralGridComponent extends GridBaseComponent<UserEditFormType> implements OnInit {

  @ViewChild(UserEditFormComponent)
  public userEditComponent: UserEditFormComponent;

  public filterOptions: FilterSettingsModel;

  private usersSelected = [];


  public selectionOptions = {
    type: 'Multiple',
    persistSelection: true
  };

  constructor(
    private formsCoordinationService: ImportConfigurationEditFormsCoordinatorService,
    private fb: FormBuilder,
    private userManagementService: UserManagementService,
    translateService: TranslateService,
    private spinnerService: SpinnerService,) {
    super(translateService, userManagementService);
    this.gridCommands = GridConstantsFactory.createEditOnlyGridCommands();
  }

  public ngOnInit() {
    //Initial filter for roles
    this.filterOptions = {
      type: 'Menu',
    };

    this.data = new DataManager({
      adaptor: new AvpCustomAdaptor(this.userManagementService, this.spinnerService, false, [
        new FilterDTO({
          isComplex: true,
          condition: FilterCondition.Or,
          predicates: [
            new FilterDTO({ isComplex: false, field: 'Roles', operator: FilterOperator.Equal, value: Role.InterfaceServiceAccess.toString() }),
          ]
        }),
        new FilterDTO({ isComplex: false, field: 'ServiceAccountPassword', operator: FilterOperator.NotEqual, value: '' })
      ])
    });

    this.isActiveColumnName = 'enabled';
    this.editDialogTitle = 'i18n.base.user-add-modal.titleEdit';
    this.addDialogTitle = 'i18n.base.user-management.buttons.add-service-access';
    this.addIndexFindColumnName = 'userName';

    this.updateSelectedClient(this.formsCoordinationService.configuration?.client);

    this.formsCoordinationService.configurationUpdated.subscribe(() => {
      this.updateSelectedClient(this.formsCoordinationService.configuration?.client);
      this.grid.refresh();
    });
  }

  public override actionBegin(args) {
    super.actionBegin(args);
    if (args.requestType === 'refresh') {
      this.usersSelected = [];
    }
  }

  public addUser(): void {
    this.userManagementService.selectedClient.id = this.formsCoordinationService.configuration?.client?.id;
    this.onAdd();
    if (this.gridEditForm) {
      this.gridEditForm.get('client').setValue(this.formsCoordinationService.configuration?.client);
    }

  }

  public rowDataBound(args: RowDataBoundEventArgs): void {
    //Preselect tupels
    const userAssignments = this.formsCoordinationService.configuration.userAssignments;
    if (userAssignments.find(ua => ua.userId === (args.data as ImportConfigurationUserAssignmentDTO).userId)) {
      const rowIndex = Number((args.row as any)?.dataset?.rowindex);
      if (!isNaN(rowIndex)) {
        this.usersSelected.push(rowIndex);
      }
    }
  }

  public dataBound(args) {

    const userAssignments =
      this.formsCoordinationService.configuration.userAssignments;

    if (!userAssignments?.length) {
      return;
    }

    const selectedIndexes: number[] = [];

    const records = this.grid.currentViewData as ImportConfigurationUserAssignmentDTO[];

    records.forEach((record, index) => {
      const exists = userAssignments.some(
        ua => ua.userId === record.userId
      );

      if (exists) {
        selectedIndexes.push(index);
      }
    });

    this.grid.selectRows(selectedIndexes);
  }

  rowSelectedChanged() {
    this.formsCoordinationService.configurationFormGeneral.controls.userAssignments.setValue((this.grid.getSelectedRecords() as Array<ImportConfigurationUserAssignmentDTO>));
  }

  protected override getDataToSave(): any {
    return this.mapToUserInfoDTO(this.gridEditForm);
  }

  protected initializeEditForm(data: UserInfoDTO): void {
    let userName = data.userName;
    this.gridEditForm = this.fb.group<UserEditFormType>({
      userId: this.fb.control<string>(data.userId),
      firstname: this.fb.control<string>(null),
      lastname: this.fb.control<string>(null),
      language: this.fb.control<string>(null),
      email: this.fb.control<string>(data.email, [Validators.required, CustomValidatorsFactory.emailValidation()]),
      phoneNumber: this.fb.control<string>(null),
      enabled: this.fb.control<boolean>(data.enabled),
      client: this.fb.control<ClientDTO>({ value: data.client, disabled: true }, Validators.required),
      roles: this.fb.control<Role[]>(data.roles ?? [], Validators.required),
      userName: this.fb.control<string>({ value: userName, disabled: data.userId ? true : false }, Validators.required),
      password: this.fb.control<string>(data.password, Validators.required)
    });
    this.gridEditForm.controls.client.setValue(data.client != null && data.client.id != null ? data.client : this.formsCoordinationService.configuration?.client);
    this.gridEditForm.controls.roles.setValue(data.roles ?? [Role.InterfaceServiceAccess]);
  }

  protected getEditDialogTitleParams(rowData): object {
    return { param: rowData.name };
  }

  private mapToUserInfoDTO(form: FormGroup<UserEditFormType>): CreateInterfaceUserDTO | UpdateInterfaceUserInfoDTO {
    var formData = form.getRawValue();
    let dto = null;
    if (!formData.userId) {
      dto = new CreateInterfaceUserDTO();
    } else {
      dto = new UpdateInterfaceUserInfoDTO();
      dto.userId = formData.userId;
      dto.enabled = formData.enabled;
    }
    dto.clientId = formData.client.id;
    dto.userName = formData.userName;
    dto.password = formData.password;
    dto.email = formData.email;
    dto.roles = formData.roles;
    return dto;
  }

  private updateSelectedClient(client: ClientDTO): void {
    if (client?.id) {
      this.userManagementService.selectedClient = client;
    }
  }
}
